import { useQuery } from "@tanstack/react-query";
import { aiChatQueryKeys } from "@/lib/data/react-query/ai-chat";
import type { ChatApi } from "@/lib/types/ai-chat";
import {
  layoutFromItem,
  mergeChatLayout,
  type ChatLayoutData,
} from "./chat-layout";

export function useChatLayout(
  threadId: string | undefined,
  api: ChatApi,
  enabled: boolean,
) {
  return useQuery({
    queryKey: aiChatQueryKeys.layout(threadId ?? "none"),
    enabled: enabled && Boolean(threadId && api.getLayout),
    queryFn: async ({ signal }): Promise<ChatLayoutData> => {
      if (!threadId || !api.getLayout)
        throw new Error("Conversation layout is unavailable.");
      let cursor: string | undefined;
      let result: ChatLayoutData = { items: [], revision: 0, complete: false };
      let baseline: number | undefined;
      do {
        const page = await api.getLayout(threadId, {
          cursor,
          limit: 1000,
          signal,
        });
        baseline ??= page.revision;
        if (page.threadId !== threadId || page.revision !== baseline)
          throw new Error("The conversation layout changed. Please try again.");
        result = mergeChatLayout(result, {
          items: page.items,
          revision: page.revision,
          complete: false,
        });
        if (page.hasMore && (!page.nextCursor || page.nextCursor === cursor))
          throw new Error("Conversation layout pagination could not continue.");
        cursor = page.hasMore ? (page.nextCursor ?? undefined) : undefined;
      } while (cursor);
      let changeCursor = String(baseline);
      while (!signal.aborted) {
        const changes = await api.getChanges(threadId, {
          cursor: changeCursor,
          limit: 100,
          signal,
        });
        if (changes.resyncRequired)
          throw new Error(
            "The conversation changed while loading history. Please try again.",
          );
        result = mergeChatLayout(result, {
          items: changes.items.map(layoutFromItem),
          revision: Math.max(
            result.revision,
            ...changes.items.map((item) => item.revision),
          ),
          complete: false,
        });
        if (!changes.hasMore) break;
        if (changes.nextCursor === changeCursor)
          throw new Error("Conversation updates could not continue.");
        changeCursor = changes.nextCursor;
      }
      if (signal.aborted)
        throw new DOMException("History loading was cancelled", "AbortError");
      return { ...result, complete: true };
    },
    structuralSharing: (previous, incoming) =>
      mergeChatLayout(
        previous as ChatLayoutData | undefined,
        incoming as ChatLayoutData,
      ),
    staleTime: (query) => (query.state.data?.complete ? 60_000 : 0),
    refetchOnWindowFocus: false,
  });
}
